/**
 * Print preview for printable products (views/partial/print-preview.twig).
 *
 * Shows a customer exactly what will be printed: their photo cropped to the
 * product's real proportions, at its real size against a background scene, with
 * a warning when the file is too small to print sharply. The photo always fills
 * the piece; what the customer controls is which part of it survives, by
 * dragging and zooming it inside the frame. The resulting slice is carried
 * through to the order so the preview is a promise rather than a picture.
 *
 * It takes over the gallery's main-image slot rather than opening a dialog. On
 * desktop that slot is a sticky column beside the upload grid, so the preview
 * is large and stays in view while the customer keeps working, and the product
 * thumbnail rail beside it doubles as the way back. Below 1000px the same panel
 * becomes a full-screen sheet: there the gallery sits far above the grid, so
 * swapping it in place would scroll the customer away from what they were doing.
 * Only the sheet behaves modally (scroll lock, focus trap, focus restore).
 *
 * The image source is the blob URL photo-upload.js already created for the
 * thumbnail, so there is no network round trip and nothing has to be readable
 * from the deny-all staging directory. Geometry arrives as
 * window.SnapmagnetiqueUpload.print (PrintSpec::toArray), and is null when the
 * product has no print size, in which case this module does nothing at all.
 *
 * Talks to the uploader through sm:-namespaced events on [data-product-config],
 * the same way product.js and add-to-cart.js do:
 *   in   sm:preview          { index, photos: [{ index, url, fx, fy, zoom, w, h }] }
 *   out  sm:cropchange       { index, rect, zoom, x, y }
 *   out  sm:preview-replace  { index }
 *   out  sm:preview-remove   { index }
 */

const MM_PER_INCH = 25.4;

/** Anything below half the target resolution gets a warning. */
const LOW_RES_FRACTION = 0.5;

/** Matches the min-[1000px] breakpoint the product layout switches on. */
const INLINE_QUERY = '(min-width: 1000px)';

/** Kept in step with the duration-200 utility on the two fading elements. */
const FADE_MS = 200;

/** How far one press of an on-screen arrow moves the photo, in percent. */
const NUDGE_PCT = 8;

/**
 * Least headroom worth offering a zoom slider for. A file that can only take
 * another few percent before it prints soft has nothing useful to give, and a
 * slider that barely moves reads as broken.
 */
const MIN_USEFUL_ZOOM = 1.15;

/**
 * How much of the scene window the piece should try to fill, as a percentage of
 * its width. At true scale a 63mm magnet is about 7% of a whole-fridge photo,
 * which reads as a speck; magnifying the whole scene until the piece reaches
 * roughly this keeps the proportions honest while making the mockup legible.
 */
const SCENE_TARGET_PCT = 32;

/**
 * How far past its own pixels the background may be enlarged before we stop
 * zooming. A fridge door is a large flat surface with almost no fine detail, so
 * it takes a modest upscale without looking soft, and that buys a noticeably
 * closer view than staying at one source pixel per screen pixel.
 */
const SCENE_MAX_UPSCALE = 1.75;

/**
 * How much of a photo has to be lost, or gained as border, before it is worth
 * saying so. Under this a shape mismatch is not something anyone would notice.
 */
const SHAPE_NOTE_THRESHOLD = 0.15;

/** Tone classes for the single notice line. */
const NOTICE_WARN = ['border-status-warn-border', 'bg-status-warn-bg', 'text-status-warn-ink'];
const NOTICE_INFO = ['border-border', 'bg-surface-warm', 'text-body'];

const FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

/** Minimal sprintf for the %1$d / %2$d / %d / %s patterns used in our strings. */
function format(template, ...args) {
  let i = 0;
  return template
    .replace(/%(\d+)\$[ds]/g, (_, n) => String(args[Number(n) - 1] ?? ''))
    .replace(/%[ds]/g, () => String(args[i++] ?? ''));
}

/**
 * Resolution the source file actually delivers at the printed size, in dots per
 * inch, or null when the geometry or the pixel dimensions are unknown.
 *
 * The photo is scaled up until it covers the piece and the overflow is thrown
 * away, so the tighter axis sets the scale: the larger mm-per-pixel. Both axes
 * then share that one factor, so a single number describes the print. Where the
 * customer positioned the photo does not change this, only which pixels are
 * kept.
 */
export function effectiveDpi(print, width, height, zoom = 1) {
  if (!print || !(width > 0) || !(height > 0)) {
    return null;
  }
  const rect = cropRect(print, width, height, zoom, 50, 50);
  const sourcePx = rect.w * width;

  return sourcePx / (print.width_mm / MM_PER_INCH);
}

/**
 * How much of the photo survives the crop onto the piece, as a fraction. A
 * photo already the right shape returns 1; the rest is what the customer is
 * choosing between when they drag.
 */
export function fitFraction(print, width, height) {
  if (!print || !(width > 0) || !(height > 0)) {
    return 1;
  }
  const printRatio = print.width_mm / print.height_mm;
  const imageRatio = width / height;

  return Math.min(printRatio / imageRatio, imageRatio / printRatio);
}

/**
 * The slice of a photo that gets printed, as fractions of the photo itself:
 * {x, y, w, h}, all 0..1.
 *
 * At zoom 1 this is the largest rectangle of the piece's shape that fits, which
 * is what `cover` does. Zooming in shrinks it, which is what creates room to
 * move on both axes: at zoom 1 exactly one axis has any slack, because the
 * other is already using the whole photo.
 *
 * This one function is the whole model. The preview, the grid thumbnail, the
 * order record and the admin screen all render from its output, so none of them
 * can disagree about what the customer chose.
 */
export function cropRect(print, width, height, zoom, fx, fy) {
  const printRatio = print.width_mm / print.height_mm;
  const imageRatio = width / height;

  // Cover, in fractions of the photo: one axis is 1, the other is short.
  let w = imageRatio > printRatio ? printRatio / imageRatio : 1;
  let h = imageRatio > printRatio ? 1 : imageRatio / printRatio;

  const scale = Math.max(1, zoom || 1);
  w /= scale;
  h /= scale;

  return {
    x: (1 - w) * Math.max(0, Math.min(1, fx / 100)),
    y: (1 - h) * Math.max(0, Math.min(1, fy / 100)),
    w,
    h,
  };
}

/**
 * The most a photo can be zoomed before it stops meeting the product's target
 * resolution, never below 1.
 *
 * Capping here rather than warning afterwards means the slider cannot reach a
 * setting that prints badly: a big file zooms a long way, a small one hardly at
 * all, and a file that was already too small gets no slider.
 */
export function maxZoom(print, width, height) {
  if (!print || !(width > 0) || !(height > 0)) {
    return 1;
  }
  const base = cropRect(print, width, height, 1, 50, 50);
  const sourcePx = base.w * width;
  const neededPx = (print.width_mm / MM_PER_INCH) * print.dpi;

  return Math.max(1, Math.min(4, neededPx > 0 ? sourcePx / neededPx : 1));
}

/** True when this photo will visibly soften at the printed size. */
export function isLowRes(print, width, height, zoom = 1) {
  const dpi = effectiveDpi(print, width, height, zoom);

  return dpi !== null && dpi < print.dpi * LOW_RES_FRACTION;
}

/**
 * Boot the preview. No-op without a configurator, a print geometry or the panel
 * markup, so it is safe to call on every page.
 */
export function initPrintPreview() {
  const root = document.querySelector('[data-product-config]');
  const config = window.SnapmagnetiqueUpload;
  const print = config && config.print;
  if (!root || !print) {
    return;
  }

  // Document-scoped: the panel lives in the gallery partial, which is a sibling
  // of the configurator section rather than a descendant of it.
  const panel = document.querySelector('[data-print-preview]');
  const galleryMain = document.querySelector('[data-gallery-main]');
  if (!panel || !galleryMain) {
    return;
  }

  const i18n = config.i18n || {};
  // Hidden alongside the product shot so the preview gets the whole column.
  const galleryRail = document.querySelector('[data-gallery-rail]');
  const openBtn = root.querySelector('[data-preview-open]');
  const slotGrid = root.querySelector('[data-slot-grid]');

  const dragSurface = panel.querySelector('[data-preview-drag]');
  const positionControls = panel.querySelector('[data-preview-position-controls]');
  const recentreBtn = panel.querySelector('[data-preview-recentre]');
  const nudgeButtons = Array.from(panel.querySelectorAll('[data-preview-nudge]'));
  const zoomInput = panel.querySelector('[data-preview-zoom]');
  const zoomRow = panel.querySelector('[data-preview-zoom-row]');
  const frameImg = panel.querySelector('[data-preview-img]');
  const sceneImg = panel.querySelector('[data-preview-scene-img]');
  const position = panel.querySelector('[data-preview-position]');

  const quality = panel.querySelector('[data-preview-quality]');
  const closeBtn = panel.querySelector('[data-preview-close]');
  const prevBtn = panel.querySelector('[data-preview-prev]');
  const nextBtn = panel.querySelector('[data-preview-next]');
  const replaceBtn = panel.querySelector('[data-preview-replace]');
  const removeBtn = panel.querySelector('[data-preview-remove]');
  const scenePane = panel.querySelector('[data-preview-pane="scene"]');
  const sceneStage = panel.querySelector('[data-preview-scene-stage]');
  const tabs = Array.from(panel.querySelectorAll('[data-preview-tab]'));
  const panes = Array.from(panel.querySelectorAll('[data-preview-pane]'));


  // The set as it stood when the preview opened. Removing a photo closes rather
  // than resyncing, so this cannot go stale underneath the customer.
  let photos = [];
  let cursor = 0;
  let openedFrom = -1;
  let fading = null;
  // Intent, not appearance. The fade runs on a timer, so during those 200ms the
  // DOM still says "closed" while the customer has already opened it; reading
  // the classes here would let a fast open-then-close land inside out.
  let opened = false;

  const isOpen = () => opened;
  /** True below the breakpoint, where the panel presents as a modal sheet. */
  const isSheet = () => !window.matchMedia(INLINE_QUERY).matches;
  const current = () => photos[cursor] || null;

  /** The slice of the current photo that will print. */
  const rectFor = (photo) => cropRect(print, photo.w, photo.h, photo.zoom, photo.fx, photo.fy);

  /**
   * Point an <img> at a photo and frame it exactly as it will print.
   *
   * Everything is a percentage of the window, so this needs no measurement and
   * survives any resize: the photo is blown up to `1 / rect.w` of the window
   * and slid back by its offset into it.
   */
  const paint = (img, photo) => {
    if (!img || !photo) {
      return;
    }
    if (img.src !== photo.url) {
      img.src = photo.url;
    }

    // Until the file has decoded we do not know its shape, and the crop
    // arithmetic would divide by zero and emit "NaN%", which the browser drops
    // and which leaves a wide photo letterboxed in the very view meant to prove
    // it fills the magnet. Fall back to a plain centred cover, which is what an
    // unadjusted photo resolves to anyway; the load handler repaints.
    if (!(photo.w > 0) || !(photo.h > 0)) {
      img.style.maxWidth = 'none';
      img.style.maxHeight = 'none';
      img.style.objectFit = 'cover';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.left = '0%';
      img.style.top = '0%';
      return;
    }
    img.style.objectFit = '';

    const rect = rectFor(photo);
    // Inline, and inline max-width in particular: WooCommerce ships an
    // unlayered `img { max-width: 100% }`, which beats Tailwind's layered
    // max-w-none utility and would clamp a zoomed photo back to the frame
    // width while its height kept going, squashing it.
    img.style.maxWidth = 'none';
    img.style.maxHeight = 'none';
    img.style.width = `${100 / rect.w}%`;
    img.style.height = `${100 / rect.h}%`;
    img.style.left = `${(-rect.x * 100) / rect.w}%`;
    img.style.top = `${(-rect.y * 100) / rect.h}%`;
  };

  /**
   * Pixels of travel available on each axis, for turning a drag into percent.
   *
   * At zoom 1 exactly one axis has any, because the other already uses the
   * whole photo. Zooming in gives both some, which is what lets the customer
   * move the photo freely.
   */
  const overflow = () => {
    const photo = current();
    if (!dragSurface || !photo || !(photo.w > 0) || !(photo.h > 0)) {
      return { x: 0, y: 0 };
    }
    // clientWidth/Height, not getBoundingClientRect: the frame has a border, and
    // the photo inside is positioned against the content box. Measuring the
    // border box scales every drag by 506/504 and leaves the focal point short
    // of the ends the arrows say are reachable.
    const boxW = dragSurface.clientWidth;
    const boxH = dragSurface.clientHeight;
    if (!boxW) {
      return { x: 0, y: 0 };
    }
    const rect = rectFor(photo);
    // Displayed photo size in window pixels, minus the window itself.
    return {
      x: boxW / rect.w - boxW,
      y: boxH / rect.h - boxH,
    };
  };

  /**
   * Offer the positioning control only when there is something to position.
   *
   * Split out of showAt() because it needs a measurable frame: during the
   * opening fade the panel is still display:none, so anything measured then
   * reports zero and the control would be hidden for every photo.
   */
  const syncPositionControls = () => {
    if (!positionControls) {
      return;
    }
    const photo = current();
    if (!photo) {
      return;
    }

    const room = overflow();
    const ceiling = maxZoom(print, photo.w, photo.h);
    const canMove = room.x > 1 || room.y > 1;
    const canZoom = ceiling >= MIN_USEFUL_ZOOM;

    positionControls.classList.toggle('hidden', !canMove && !canZoom);
    positionControls.classList.toggle('flex', canMove || canZoom);
    if (dragSurface) {
      dragSurface.classList.toggle('cursor-grab', canMove);
      dragSurface.classList.toggle('active:cursor-grabbing', canMove);
    }

    // The zoom ceiling is this file's own: a big photo zooms a long way, a
    // small one hardly at all, and one already at its limit gets no slider.
    if (zoomRow && zoomInput) {
      zoomRow.classList.toggle('hidden', !canZoom);
      zoomRow.classList.toggle('flex', canZoom);
      zoomInput.max = String(Math.round(ceiling * 100) / 100);
      zoomInput.value = String(photo.zoom);
    }

    // Arrows follow whichever axis has the most room, so they are useful at
    // zoom 1 (where only one axis moves) and after zooming (where both do).
    const vertical = room.y >= room.x;
    nudgeButtons.forEach((button) => {
      const back = button.dataset.previewNudge === '-1';
      const icon = button.querySelector('[data-preview-nudge-icon]');
      if (icon) {
        icon.classList.toggle('rotate-90', vertical);
      }
      button.setAttribute(
        'aria-label',
        (vertical ? (back ? i18n.nudgeUp : i18n.nudgeDown) : (back ? i18n.nudgeLeft : i18n.nudgeRight)) || ''
      );
      // Grey out the end the photo is already against. Moving it up or left
      // raises the focal point, so that is the button that runs out at 100.
      const at = vertical ? photo.fy : photo.fx;
      const axisRoom = vertical ? room.y : room.x;
      button.disabled = axisRoom <= 1 || (back ? at >= 99.99 : at <= 0.01);
    });
  };

  /**
   * Reframe the current photo, clamped, and tell the uploader.
   *
   * The uploader is sent the resulting rectangle rather than the zoom and
   * position that produced it: the rectangle is what gets printed, and it is
   * complete on its own.
   */
  const reframe = (x, y, zoom) => {
    const photo = current();
    if (!photo) {
      return;
    }
    const nz = Math.max(1, Math.min(maxZoom(print, photo.w, photo.h), zoom ?? photo.zoom));
    const nx = Math.max(0, Math.min(100, x ?? photo.fx));
    const ny = Math.max(0, Math.min(100, y ?? photo.fy));
    if (
      Math.abs(nx - photo.fx) < 0.01 &&
      Math.abs(ny - photo.fy) < 0.01 &&
      Math.abs(nz - photo.zoom) < 0.001
    ) {
      return;
    }
    photo.fx = nx;
    photo.fy = ny;
    photo.zoom = nz;
    paint(frameImg, photo);
    paint(sceneImg, photo);
    syncPositionControls();
    showNotice(photo);
    root.dispatchEvent(
      new CustomEvent('sm:cropchange', {
        detail: { index: photo.index, rect: rectFor(photo), zoom: nz, x: nx, y: ny },
      })
    );
  };

  /**
   * What to tell the customer about this photo, or null when there is nothing
   * worth saying.
   *
   * Both cases depend on the fit, so flipping the toggle can change or clear
   * the message: `cover` spends pixels and trims edges, `contain` keeps every
   * pixel and adds a border instead.
   */
  const notice = (photo) => {
    if (isLowRes(print, photo.w, photo.h, photo.zoom)) {
      return { text: i18n.lowRes || '', tone: 'warn' };
    }

    // How much of the photo is left out, zoom included.
    const rect = cropRect(print, photo.w, photo.h, photo.zoom, photo.fx, photo.fy);
    const lost = 1 - rect.w * rect.h;
    if (!(lost > SHAPE_NOTE_THRESHOLD)) {
      return null; // Close enough to the right shape to say nothing.
    }

    // A photo wider than the piece loses its sides to the crop; a taller one
    // loses its top and bottom. Either way the customer can drag to choose
    // which part goes.
    const wide = photo.w / photo.h > print.width_mm / print.height_mm;

    return {
      text: format((wide ? i18n.cropSides : i18n.cropEnds) || '', Math.round(lost * 100)),
      tone: 'info',
    };
  };

  /**
   * Put the one notice line in the right state.
   *
   * Resolution outranks shape: a photo that will print soft is a bigger problem
   * than one losing its edges, and stacking both would be noise above the buy
   * button.
   */
  const showNotice = (photo) => {
    if (!quality || !photo) {
      return;
    }
    const note = notice(photo);
    quality.textContent = note ? note.text : '';
    quality.classList.toggle('hidden', !note);
    quality.classList.remove(...NOTICE_WARN, ...NOTICE_INFO);
    if (note) {
      quality.classList.add(...(note.tone === 'warn' ? NOTICE_WARN : NOTICE_INFO));
    }
  };

  /** Repaint everything that depends on which photo is showing. */
  const showAt = (pos) => {
    if (!photos.length) {
      return;
    }
    cursor = Math.max(0, Math.min(photos.length - 1, pos));
    const photo = current();

    paint(frameImg, photo);
    paint(sceneImg, photo);

    if (position) {
      position.textContent = format(i18n.position || 'Photo %1$d of %2$d', cursor + 1, photos.length);
    }
    // Nothing to position when the photo already matches the piece: there is
    // no hidden part of it to bring into frame.
    syncPositionControls();

    showNotice(photo);

    if (prevBtn) {
      prevBtn.disabled = photos.length < 2;
    }
    if (nextBtn) {
      nextBtn.disabled = photos.length < 2;
    }
  };

  /**
   * Size and position the magnified scene so the piece sits in the middle of
   * the window.
   *
   * The zoom is capped two ways: it never exceeds what is needed to reach
   * SCENE_TARGET_PCT, and it never renders the background wider than its own
   * pixels, so a modest photo is shown smaller rather than upscaled into mush.
   * The stage is then clamped to the window edges so magnifying never reveals
   * blank space beside the photo.
   */
  const layoutScene = () => {
    const scene = print.scene;
    if (!scenePane || !sceneStage || !scene) {
      return;
    }
    const windowWidth = scenePane.clientWidth;
    if (!windowWidth || !(scene.width_pct > 0)) {
      return; // Pane still hidden, so it has no width to work from yet.
    }

    const printRatio = print.width_mm / print.height_mm;
    const windowHeight = windowWidth / printRatio;

    const wanted = SCENE_TARGET_PCT / scene.width_pct;
    const sharpest = scene.width > 0 ? (scene.width * SCENE_MAX_UPSCALE) / windowWidth : wanted;
    const zoom = Math.max(1, Math.min(wanted, sharpest));

    const stageWidth = windowWidth * zoom;
    const sceneRatio = scene.width > 0 && scene.height > 0 ? scene.width / scene.height : printRatio;
    const stageHeight = stageWidth / sceneRatio;

    const pieceWidth = stageWidth * (scene.width_pct / 100);
    const pieceHeight = pieceWidth / printRatio;
    const centreX = stageWidth * (scene.left_pct / 100) + pieceWidth / 2;
    const centreY = stageHeight * (scene.top_pct / 100) + pieceHeight / 2;

    // Keep the window covered: when the stage is smaller than the window on an
    // axis there is nothing to pan, so centre it on that axis instead.
    const clamp = (value, low, high) => (low > high ? (low + high) / 2 : Math.max(low, Math.min(high, value)));
    const x = clamp(windowWidth / 2 - centreX, windowWidth - stageWidth, 0);
    const y = clamp(windowHeight / 2 - centreY, windowHeight - stageHeight, 0);

    sceneStage.style.width = `${stageWidth}px`;
    sceneStage.style.transform = `translate(${x}px, ${y}px)`;

    // Size the piece in pixels rather than leaving its height to aspect-ratio.
    // The photo inside is positioned with percentages, and a percentage offset
    // does not resolve against an aspect-ratio height: it silently collapses to
    // zero, which showed the top of the photo here while the print tab showed
    // the middle. Two views of the same crop must never disagree.
    const piece = panel.querySelector('[data-preview-scene-piece]');
    if (piece) {
      piece.style.width = `${pieceWidth}px`;
      piece.style.height = `${pieceHeight}px`;
      const photo = current();
      if (photo) {
        paint(sceneImg, photo); // Re-frame now the piece has a real height.
      }
    }
  };

  const step = (delta) => {
    if (photos.length < 2) {
      return;
    }
    showAt((cursor + delta + photos.length) % photos.length);
  };

  const selectTab = (name) => {
    tabs.forEach((tab) => {
      tab.setAttribute('aria-selected', tab.dataset.previewTab === name ? 'true' : 'false');
    });
    panes.forEach((pane) => {
      pane.classList.toggle('hidden', pane.dataset.previewPane !== name);
    });
    if (name === 'scene') {
      layoutScene(); // Only measurable once the pane is no longer display:none.
    }
  };

  /**
   * Cross-fade the two halves of the gallery slot.
   *
   * Sequenced rather than overlapped: both elements sit in normal flow, so
   * showing them at once would briefly double the column's height. Fading one
   * out before the other in reads as a cross-fade without the lurch.
   */
  const fadeTo = (showPanel, onShown) => {
    const out = showPanel ? galleryMain : panel;
    const into = showPanel ? panel : galleryMain;

    window.clearTimeout(fading);
    out.classList.replace('opacity-100', 'opacity-0');
    // The thumbnail rail belongs to the product shot, so it goes with it.
    if (galleryRail) {
      galleryRail.classList.replace(showPanel ? 'opacity-100' : 'opacity-0', showPanel ? 'opacity-0' : 'opacity-100');
    }

    fading = window.setTimeout(() => {
      out.classList.add('hidden');
      out.classList.remove('flex');
      if (galleryRail) {
        galleryRail.classList.toggle('hidden', showPanel);
      }
      into.classList.remove('hidden');
      if (into === panel) {
        into.classList.add('flex');
      }
      // Next frame, so the browser has painted opacity-0 before it animates.
      window.requestAnimationFrame(() => {
        into.classList.replace('opacity-0', 'opacity-100');
      });
      if (onShown) {
        onShown();
      }
    }, FADE_MS);
  };

  /**
   * Return focus to the tile the customer came from.
   *
   * Deliberately resolved by slot index rather than kept as a node reference:
   * the uploader replaces a tile's DOM whenever that slot changes, so a saved
   * reference can be detached by the time we close and focusing it would
   * silently do nothing.
   */
  const restoreFocus = () => {
    const tile = openedFrom >= 0 && slotGrid
      ? slotGrid.querySelector(`[data-slot][data-index="${openedFrom}"] [data-slot-preview]`)
      : null;
    const target = tile || (openBtn && !openBtn.classList.contains('hidden') ? openBtn : null);
    if (target && typeof target.focus === 'function') {
      target.focus();
    }
  };

  const close = () => {
    if (!opened) {
      return;
    }
    opened = false;
    const wasSheet = panel.dataset.sheet !== undefined;
    delete panel.dataset.sheet;
    fadeTo(false);
    document.body.style.overflow = '';
    if (wasSheet) {
      restoreFocus();
    }
  };

  const open = (slotIndex, list) => {
    const next = Array.isArray(list) ? list.filter((p) => p && p.url) : [];
    if (!next.length) {
      return;
    }
    photos = next;
    openedFrom = slotIndex;
    const at = photos.findIndex((p) => p.index === slotIndex);

    if (!opened) {
      selectTab('print');
    }
    showAt(at === -1 ? 0 : at);

    if (opened) {
      return; // Already showing: just moved to another photo.
    }

    opened = true;

    // Only the sheet is modal. Inline, the customer is meant to keep working
    // the grid beside it, so stealing focus or locking scroll would be wrong.
    const sheet = isSheet();
    if (sheet) {
      panel.dataset.sheet = '';
      document.body.style.overflow = 'hidden';
    }

    // Both of these have to wait for the fade: the panel is display:none until
    // it completes, so focus() does nothing and the scene pane has no width to
    // measure.
    fadeTo(true, () => {
      layoutScene();
      syncPositionControls(); // Only measurable once the panel is on screen.
      if (sheet && closeBtn) {
        closeBtn.focus();
      }
    });
  };

  // -- Events --------------------------------------------------------------
  root.addEventListener('sm:preview', (event) => {
    const detail = event.detail || {};
    open(typeof detail.index === 'number' ? detail.index : -1, detail.photos);
  });

  /**
   * Keep an open preview in step with the grid behind it.
   *
   * Inline on desktop the preview is deliberately not modal, so the customer
   * can add, remove or resize the set while looking at it. Only membership
   * changes rebuild the list: a crop change round-trips through here too, and
   * rebuilding on that would swap the photo object out from under a live drag.
   */
  root.addEventListener('sm:photos', (event) => {
    if (!opened) {
      return;
    }
    const next = (event.detail && event.detail.photos) || [];
    const key = (list) => list.map((p) => `${p.index}:${p.url}`).join('|');
    if (key(next) === key(photos)) {
      return;
    }
    if (!next.length) {
      close(); // The last photo went; there is nothing left to look at.
      return;
    }
    const showing = current();
    photos = next;
    const stillThere = showing ? next.findIndex((p) => p.index === showing.index) : -1;
    if (stillThere === -1 && showing) {
      // The photo being previewed was removed or dropped by a smaller pack.
      openedFrom = next[0].index;
    }
    showAt(stillThere === -1 ? 0 : stillThere);
    syncPositionControls();
    layoutScene();
  });

  if (openBtn) {
    openBtn.addEventListener('click', () => {
      root.dispatchEvent(new CustomEvent('sm:preview-request', { detail: { index: -1 } }));
    });
  }

  if (closeBtn) {
    closeBtn.addEventListener('click', close);
  }
  if (prevBtn) {
    prevBtn.addEventListener('click', () => step(-1));
  }
  if (nextBtn) {
    nextBtn.addEventListener('click', () => step(1));
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => selectTab(tab.dataset.previewTab));
  });

  // -- Drag to position ------------------------------------------------------
  // Pointer events cover mouse, touch and pen in one path; setPointerCapture
  // keeps the drag alive when the finger leaves the frame, which happens
  // constantly when panning to an edge.
  if (dragSurface) {
    let dragging = null;

    dragSurface.addEventListener('pointerdown', (event) => {
      const photo = current();
      const room = overflow();
      if (!photo || (room.x <= 1 && room.y <= 1)) {
        return;
      }
      dragging = { id: event.pointerId, x: event.clientX, y: event.clientY, fx: photo.fx, fy: photo.fy, room };
      dragSurface.setPointerCapture(event.pointerId);
      event.preventDefault();
    });

    dragSurface.addEventListener('pointermove', (event) => {
      if (!dragging || event.pointerId !== dragging.id) {
        return;
      }
      // Dragging right reveals what is off to the left, so the focal point
      // moves the opposite way to the finger.
      const dx = dragging.room.x > 1 ? ((dragging.x - event.clientX) / dragging.room.x) * 100 : 0;
      const dy = dragging.room.y > 1 ? ((dragging.y - event.clientY) / dragging.room.y) * 100 : 0;
      reframe(dragging.fx + dx, dragging.fy + dy);
    });

    const endDrag = (event) => {
      if (dragging && event.pointerId === dragging.id) {
        dragging = null;
      }
    };
    dragSurface.addEventListener('pointerup', endDrag);
    dragSurface.addEventListener('pointercancel', endDrag);

    // Reachable without a pointer. The frame takes focus so the arrow keys
    // have somewhere to land.
    dragSurface.tabIndex = 0;
    dragSurface.addEventListener('keydown', (event) => {
      const photo = current();
      const room = overflow();
      if (!photo || (room.x <= 1 && room.y <= 1)) {
        return;
      }
      // Signs match the drag and the arrow buttons: every control moves the
      // photo, so pressing up slides the photo up and brings its lower part
      // into frame, which raises the focal point.
      const stepPct = event.shiftKey ? 10 : 2;
      const moves = {
        ArrowLeft: [stepPct, 0],
        ArrowRight: [-stepPct, 0],
        ArrowUp: [0, stepPct],
        ArrowDown: [0, -stepPct],
      };
      const move = moves[event.key];
      if (!move) {
        return;
      }
      event.preventDefault();
      event.stopPropagation(); // Arrows here nudge, they do not change photo.
      reframe(photo.fx + (room.x > 1 ? move[0] : 0), photo.fy + (room.y > 1 ? move[1] : 0));
    });
  }

  nudgeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const photo = current();
      const room = overflow();
      if (!photo) {
        return;
      }
      const step = Number(button.dataset.previewNudge) * NUDGE_PCT;
      // Moving the photo one way means moving the focal point the other, the
      // same inversion the drag handler applies.
      if (room.y >= room.x) {
        reframe(photo.fx, photo.fy - step);
      } else {
        reframe(photo.fx - step, photo.fy);
      }
    });
  });

  if (recentreBtn) {
    // Centre also drops the zoom: one button that puts a photo back to the
    // largest, safest crop it can have.
    recentreBtn.addEventListener('click', () => reframe(50, 50, 1));
  }

  if (zoomInput) {
    zoomInput.addEventListener('input', () => {
      reframe(null, null, Number(zoomInput.value));
    });
  }

  if (replaceBtn) {
    replaceBtn.addEventListener('click', () => {
      const photo = current();
      if (!photo) {
        return;
      }
      // Close first: the uploader opens the native file dialog, which must not
      // appear behind a scroll-locked sheet.
      close();
      root.dispatchEvent(new CustomEvent('sm:preview-replace', { detail: { index: photo.index } }));
    });
  }

  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      const photo = current();
      if (!photo) {
        return;
      }
      close();
      root.dispatchEvent(new CustomEvent('sm:preview-remove', { detail: { index: photo.index } }));
    });
  }

  // The zoom depends on the rendered width, so it has to be recomputed when the
  // column resizes or the layout flips between inline and sheet.
  window.addEventListener('resize', () => {
    if (isOpen()) {
      layoutScene();
      syncPositionControls();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen()) {
      return;
    }
    if (event.key === 'Escape') {
      close();
      return;
    }
    // Arrows only while the customer is actually inside the preview. Inline it
    // shares the page with the rest of the configurator, and hijacking arrow
    // keys from everything else would be rude.
    if (!panel.contains(document.activeElement)) {
      return;
    }
    if (event.key === 'ArrowLeft') {
      step(-1);
    } else if (event.key === 'ArrowRight') {
      step(1);
    }
  });

  // Trap Tab inside the sheet, which is the only presentation that is modal.
  panel.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !isOpen() || panel.dataset.sheet === undefined) {
      return;
    }
    const focusables = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    if (!focusables.length) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
}
